import React from 'react';
import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column,
  GridDataStateChangeEvent
} from '@progress/kendo-react-grid';
import { useToggle } from 'usehooks-ts';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { Button } from '@progress/kendo-react-buttons';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';

import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { GetAsync } from '../services/api';
import {
  useZonesLibrary,
  useDeleteZoneById,
  useDeleteGlobalZoneById
} from '../hooks/services/use-zones-library';
import { downloadJSON } from '../utils/files';
import { ZoneCardView } from './zone-cardview';
import { ThemeColor } from '../types/theme-color';
import { useToast } from '../contexts/toast-context';
import PageToolbar from '../components/page-toolbar';
import HelpSupport from '../components/help-support';
import { ColumnMenu } from '../components/columnMenu';
import { AddZoneLibraryAsync } from '../services/zone';
import { ImportZoneWindow } from './zone-import-window';
import { NoRecords } from '../components/grid/no-records';
import YesNoDialog from '../components/dialogs/yes-no-dialog';
import { ZoneResponse } from '../types/responses/zone-response';
import { DefaultGridSettings } from '../constants/grid-settings';
import { ZonePreviewWindow } from './zone-library-preview/window';
import { ZoneCopyToGameWindow } from './zone-copy-to-game-window';
import { ZoneLibrary } from '../types/responses/zone-library-response';
import ActionButtonsCell from '../components/grid/action-buttons-cell';
import { formatDateToDDMMYYYY, getCurrentTimeStamp } from '../utils/date';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

export const ZoneLibraryGrid: React.FC<{
  isGlobal?: boolean;
  isAdmin?: boolean;
}> = ({ isGlobal, isAdmin }) => {
  const { onToastOpen } = useToast();

  const [dataState, setDataState] = React.useState<State>(initialDataState);
  const [gridMode, setGridMode] = React.useState<'grid' | 'card'>('grid');
  const [showDeleteModal, setShowDeleteModal] = React.useState<string>('');
  const [showPreviewModal, setShowPreviewModal] = React.useState<string>('');
  const [showCopyToGameModal, setShowCopyToGameModal] =
    React.useState<string>('');
  const [showImportModal, toggleImportModal] = useToggle();

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const [copyZoneId, setCopyZoneId] = React.useState<string | null>(null);
  const [copyZoneName, setCopyZoneName] = React.useState('');
  const [copyZoneDescription, setCopyZoneDescription] = React.useState('');
  const [isCopying, setIsCopying] = React.useState(false);

  const handleCopyZoneSubmit = async () => {
    if (!copyZoneId || isCopying) return;
    try {
      setIsCopying(true);

      const fullZone = await GetAsync<ZoneLibrary>(
        `library/zones/${copyZoneId}`
      );

      const payload: ZoneResponse = {
        name: copyZoneName,
        description: copyZoneDescription,
        previewImageUrl: (fullZone as any).previewImageUrl ?? '',
        content: fullZone.content
      };

      await AddZoneLibraryAsync(payload);
      onToastOpen({
        title: 'Success',
        body: 'Zone map copied to library.',
        style: 'success'
      });
      setCopyZoneId(null);
      mutateLibrary();
    } catch (error) {
      onToastOpen({
        title: 'Error',
        body: 'Failed to copy zone map to library.',
        style: 'error'
      });
    } finally {
      setIsCopying(false);
    }
  };

  const {
    data,
    isLoading,
    mutate: mutateLibrary
  } = useZonesLibrary(toDataSourceRequestString(dataState), isGlobal);
  const { isMutating: isLoadingDelete, trigger: triggerDelete } =
    useDeleteZoneById();
  const { isMutating: isLoadingDeleteGlobal, trigger: triggerDeleteGlobal } =
    useDeleteGlobalZoneById();

  const DateCell = (props: GridCellProps, type: 'created' | 'modified') => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(
          props.dataItem[
            type === 'created' ? 'createdDateUtc' : 'modifiedDateUtc'
          ]
        )}
      </td>
    );
  };

  const CellEllipsisAndTooltip = (props: GridCellProps, field: string) => {
    return (
      <td title={props.dataItem[field]} className={'k-table-td'}>
        <div className={'grid-ellipsis-cell'}>
          {props.dataItem[field] === null ? 'Imported' : props.dataItem[field]}
        </div>
      </td>
    );
  };

  const ActionButtonsCellWrapper = (props: GridCellProps) => (
    <ActionButtonsCell
      {...props}
      actions={[
        {
          onClick: () => setShowPreviewModal(props.dataItem['id']),
          icon: 'visibility',
          themeColor: 'primary',
          title: 'View',
          isDisable: showPreviewModal !== ''
        },
        ...(!isGlobal
          ? [
              {
                onClick: () => handleSingleZoneExport(props.dataItem['id']),
                icon: 'download_for_offline',
                themeColor: 'info' as ThemeColor,
                title: 'Export to JSON'
              },
              {
                onClick: () => setShowCopyToGameModal(props.dataItem['id']),
                icon: 'library_add_check',
                themeColor: 'primary' as ThemeColor,
                title: 'Copy Zone Map to Game'
              },
              {
                onClick: () => setShowDeleteModal(props.dataItem['id']),
                icon: 'delete',
                themeColor: 'error' as ThemeColor,
                title: 'Delete'
              }
            ]
          : []),
        ...(isGlobal && !isAdmin
          ? [
              {
                onClick: () => {
                  setCopyZoneName(props.dataItem['name'] ?? '');
                  setCopyZoneDescription(props.dataItem['description'] ?? '');
                  setCopyZoneId(props.dataItem['id']);
                },
                icon: 'library_add_check',
                themeColor: 'primary' as ThemeColor,
                title: 'Copy to My Zone Map Library'
              }
            ]
          : []),
        ...(isGlobal && isAdmin
          ? [
              {
                onClick: () => setShowDeleteModal(props.dataItem['id']),
                icon: 'delete',
                themeColor: 'error' as ThemeColor,
                title: 'Delete'
              }
            ]
          : [])
      ]}
    />
  );

  const handleSingleZoneExport = async (zoneId: string) => {
    try {
      const zoneById = await GetAsync<ZoneLibrary>(`zones/${zoneId}`);

      const zoneName = zoneById.name ?? 'zone';
      const formattedTimestamp = getCurrentTimeStamp();
      downloadJSON(
        zoneById.content,
        `zone-${zoneName}-${formattedTimestamp}.json`
      );

      onToastOpen({
        title: 'Success',
        body: 'Zone map exported.',
        style: 'success'
      });
    } catch (error) {
      onToastOpen({
        title: 'Error',
        body: 'Failed to export zone map.',
        style: 'error'
      });
    }
  };

  const handleDelete = async (zoneId: string) => {
    try {
      isAdmin
        ? await triggerDeleteGlobal({ id: zoneId })
        : await triggerDelete({ id: zoneId });

      onToastOpen({
        title: 'Success',
        body: 'Zone map deleted.',
        style: 'success'
      });

      mutateLibrary();
      isAdmin && setShowPreviewModal('');
    } catch (error) {
      onToastOpen({
        title: 'Error',
        body: 'Failed to delete zone map.',
        style: 'error'
      });
    }

    setShowDeleteModal('');
  };

  const pageTitle = React.useMemo(() => {
    if (isGlobal) return 'Global Zone Maps';

    return 'Zone Maps';
  }, [isAdmin, isGlobal]);

  React.useEffect(() => {
    mutateLibrary();
  }, [dataState]);

  return (
    <>
      <PageToolbar title={pageTitle}>
        {!isGlobal && (
          <HelpSupport
            title="My zone map library guide"
            url="https://forum.catalystglobal.com/t/5185"
          />
        )}
      </PageToolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search assets'}
          columnsToSearch={['name']}
          exportRef={gridExportRef}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode as 'grid' | 'card');
          }}>
          {!isGlobal && (
            <Button
              onClick={toggleImportModal}
              className={'ms-2'}
              title={'Import from json'}>
              <span className="material-symbols-outlined">upload</span>
            </Button>
          )}
        </GridToolBar>

        <ExcelExport
          data={data?.data ?? []}
          ref={gridExportRef}
          fileName={isGlobal ? 'global-zone-maps.xlsx' : 'my-zone-maps.xlsx'}>
          <ExcelExportColumn field="name" title="Name" width={250} />
          <ExcelExportColumn
            field="description"
            title="Description"
            width={350}
          />
          <ExcelExportColumn
            field="createdDateUtc"
            title="Created"
            width={150}
          />
          {isGlobal ? (
            <ExcelExportColumn
              field="organisation"
              title="Organisation Name"
              width={250}
            />
          ) : (
            <>
              <ExcelExportColumn field="status" title="Status" width={150} />
              <ExcelExportColumn field="source" title="Source" width={200} />
            </>
          )}
        </ExcelExport>

        {gridMode === 'grid' && (
          <Grid
            className={'k-grid--with-5-actions'}
            id={'grid-zonelibrary'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable
            data={data}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'name'}
              title={'Name'}
              columnMenu={ColumnMenu}
              cells={{ data: (props) => CellEllipsisAndTooltip(props, 'name') }}
            />
            <Column
              field={'description'}
              title={'Description'}
              columnMenu={ColumnMenu}
              cells={{
                data: (props) => CellEllipsisAndTooltip(props, 'description')
              }}
            />
            <Column
              field={'createdDateUtc'}
              title={'Created'}
              width={120}
              columnMenu={ColumnMenu}
              cells={{ data: (props) => DateCell(props, 'created') }}
            />
            {isGlobal && (
              <Column
                field={'organisation'}
                title={'Organisation Name'}
                columnMenu={ColumnMenu}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'organisation')
                }}
              />
            )}
            {!isGlobal && (
              <Column
                field={'status'}
                title={'Status'}
                width={110}
                columnMenu={ColumnMenu}
              />
            )}
            {!isGlobal && (
              <Column
                field={'source'}
                title={'Source'}
                columnMenu={ColumnMenu}
                cells={{
                  data: (props) => CellEllipsisAndTooltip(props, 'source')
                }}
              />
            )}
            <Column
              title={'Actions'}
              width={210}
              cells={{ data: ActionButtonsCellWrapper }}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        )}

        {gridMode === 'card' && (
          <ZoneCardView
            isGlobal={isGlobal}
            isAdmin={isAdmin}
            total={data?.total}
            data={data?.data ?? []}
            onPreview={(id) => setShowPreviewModal(id.toString())}
            onDelete={(id) => setShowDeleteModal(id.toString())}
            onExport={(id) => handleSingleZoneExport(id.toString())}
            onCopy={(id) => {
              const cardZone = data?.data?.find((z) => z.id === id);
              if (cardZone) {
                setCopyZoneName(cardZone.name ?? '');
                setCopyZoneDescription(cardZone.description ?? '');
                setCopyZoneId(id.toString());
              }
            }}
            onCopyToGame={(id) => setShowCopyToGameModal(id.toString())}
          />
        )}
      </div>

      {showImportModal && <ImportZoneWindow onClose={toggleImportModal} />}

      {showCopyToGameModal && (
        <ZoneCopyToGameWindow
          zoneId={showCopyToGameModal}
          onClose={() => setShowCopyToGameModal('')}
        />
      )}

      {showPreviewModal && (
        <ZonePreviewWindow
          zoneId={showPreviewModal}
          onClose={() => {
            setShowPreviewModal('');
            mutateLibrary();
          }}
          isGlobal={isGlobal}
          isAdmin={isAdmin}
          onDelete={(zoneId) => setShowDeleteModal(zoneId)}
        />
      )}

      {showDeleteModal && (
        <YesNoDialog
          title={'Confirm removal'}
          isDisabled={isLoadingDelete || isLoadingDeleteGlobal}
          onConfirm={(action) =>
            action === 'yes'
              ? handleDelete(showDeleteModal)
              : setShowDeleteModal('')
          }
          onClose={() => setShowDeleteModal('')}>
          Are you sure you want to remove the zone?
        </YesNoDialog>
      )}

      {copyZoneId && (
        <Window
          title={'Copy Zone Map to My Zone Map Library'}
          onClose={() => {
            if (isCopying) return;
            setCopyZoneId(null);
          }}
          initialWidth={400}
          initialHeight={350}>
          <div className="mb-3">
            <label className="k-label">Name</label>
            <Input
              value={copyZoneName}
              onChange={(e) => setCopyZoneName(e.value)}
              disabled={isCopying}
              style={{ width: '100%' }}
            />
          </div>
          <div className="mb-3">
            <label className="k-label">Description</label>
            <TextArea
              value={copyZoneDescription}
              onChange={(e) => setCopyZoneDescription(e.value)}
              disabled={isCopying}
              style={{ width: '100%' }}
            />
          </div>
          <WindowActionsBar>
            <Button
              themeColor={'secondary'}
              disabled={isCopying}
              onClick={() => setCopyZoneId(null)}>
              Cancel
            </Button>
            <Button
              themeColor={'primary'}
              disabled={isCopying || !copyZoneName || !copyZoneDescription}
              onClick={handleCopyZoneSubmit}>
              {isCopying ? 'Copying...' : 'Copy'}
            </Button>
          </WindowActionsBar>
        </Window>
      )}
    </>
  );
};
